// Global save-status surface for the admin pricing matrix. Renders:
//   - a "Save changes" Button when there are unsaved edits (the page wires
//     `onSave` to dispatch `saveAllDirty(rows)`).
//   - a "Saving…" disabled button while a batch is in flight.
//   - a status Chip for idle ("All changes saved"), error counts, etc.
// Mirrors the active message into a visually-hidden aria-live region so
// screen readers are notified without a focus change.
import { useSelector } from 'react-redux';
import {
  Box, Button, Chip, CircularProgress, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SaveIcon from '@mui/icons-material/Save';
import { selectSaveStatusCounts } from '../../../store/pricing-editor-slice.js';

const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const pluralize = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const SaveStatusPill = ({ onSave }) => {
  const counts = useSelector(selectSaveStatusCounts);
  const { saving, dirty, error } = counts;

  let surface;
  let ariaLabel;

  if (saving) {
    ariaLabel = 'Saving pricing changes';
    surface = (
      <Button
        variant="contained"
        size="small"
        disabled
        startIcon={<CircularProgress size={14} color="inherit" />}
      >
        Saving…
      </Button>
    );
  } else if (dirty) {
    ariaLabel = `${pluralize(dirty, 'unsaved change')}. Save now.`;
    surface = (
      <Button
        variant="contained"
        color="primary"
        size="small"
        startIcon={<SaveIcon fontSize="small" />}
        onClick={onSave}
      >
        {`Save ${pluralize(dirty, 'change')}`}
      </Button>
    );
  } else if (error) {
    ariaLabel = `${pluralize(error, 'pricing save error')}`;
    surface = (
      <Chip
        icon={<ErrorIcon fontSize="small" />}
        label={pluralize(error, 'error')}
        color="error"
        variant="outlined"
        size="small"
        aria-hidden="true"
      />
    );
  } else {
    ariaLabel = 'All pricing changes saved';
    surface = (
      <Chip
        icon={<CheckCircleIcon fontSize="small" />}
        label="All changes saved"
        color="success"
        variant="outlined"
        size="small"
        aria-hidden="true"
      />
    );
  }

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      {surface}
      <Box component="span" role="status" aria-live="polite" sx={visuallyHidden}>
        <Typography component="span" variant="body2">{ariaLabel}</Typography>
      </Box>
    </Box>
  );
};

export default SaveStatusPill;
